import { ArrowUpRight } from "lucide-react";
import { PageShell, ShellSection } from "./PageShell";
import { PRODUCTS, ACTIVE_INITIATIVES, STRUCTURAL, BUD } from "../data/content";
import { StatusBadge } from "../components/common";
import { ProductCard } from "../components/ProductCard";
import { TurfTalkCard } from "../components/TurfTalkCard";
import { BudCard } from "../components/BudCard";
import { PortfolioMap } from "../components/PortfolioMap";
import { NowStrip } from "../components/NowStrip";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

export default function PortfolioPage() {
    usePageMeta(
        "Portfolio — Titan Haven Group LLC",
        "What Titan Haven Group LLC builds, operates, is developing, or is affiliated with — with maturity and relationship stated plainly.",
    );
    return (
        <PageShell
            testId="portfolio-page"
            overline="Portfolio"
            title="What Titan Haven Group LLC builds and operates."
            lede="Commercial products first, active development second, exploratory and structural work labeled as such. THG-owned products and affiliated-entity interests are distinguished plainly."
        >
            <ShellSection>
                <Reveal>
                    <p className="text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl" data-testid="venture-studio-note">
                        THG&rsquo;s portfolio is developed through an operator-led venture studio model: opportunities
                        are identified, working assets are built, and each is operated, partnered, licensed, or
                        transferred according to strategic fit.
                    </p>
                </Reveal>
            </ShellSection>
            <NowStrip />
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Portfolio map</h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                        The breadth of the company, organized by operating lane. Maturity and relationship are stated,
                        not implied.
                    </p>
                </Reveal>
                <Reveal delay={0.1} className="mt-10">
                    <PortfolioMap />
                </Reveal>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Commercial products</h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                        The commercial software tier — WarRoom Solutions products leading the company&rsquo;s
                        commercial story.
                    </p>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="portfolio-commercial-tier">
                    {PRODUCTS.filter((p) => p.category === "Commercial Software").map((p, i) => (
                        <Reveal key={p.slug} delay={i * 0.06}>
                            <ProductCard product={p} testId={`portfolio-product-${p.slug}`} compact />
                        </Reveal>
                    ))}
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">
                        Demonstrable partner product
                    </h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                        A working product in controlled demonstrations — owned and operated by SportChant, LTD., a
                        New York corporation; the rebuilt experience was designed and built with Titan Haven Group LLC.
                    </p>
                </Reveal>
                <Reveal delay={0.1} className="mt-10">
                    <TurfTalkCard spotlight />
                </Reveal>
            </ShellSection>
            <ShellSection>
                <div id="digital" className="scroll-mt-24">
                    <Reveal>
                        <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Digital Businesses</h2>
                        <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                            Owned and operated digital properties. HotelsTrips.com has been rebuilt and is active;
                            additional digital assets will be added here as they come online.
                        </p>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {PRODUCTS.filter((p) => p.category === "Digital Business").map((p) => (
                            <Reveal key={p.slug}>
                                <ProductCard product={p} testId={`portfolio-product-${p.slug}`} compact />
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.05}>
                        <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
                            <a
                                href="https://hotelstrips.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="digital-hoteltrips-link"
                                className="inline-flex items-center gap-2 text-sm text-thg-secondary hover:text-white transition-colors duration-300"
                                aria-label="HotelsTrips.com — opens in a new tab"
                            >
                                Visit HotelsTrips.com <ArrowUpRight size={15} />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </ShellSection>
            <ShellSection>
                <div id="assets" className="scroll-mt-24">
                    <Reveal>
                        <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Investment / Asset / Structural</h2>
                        <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-2xl">
                            A structural lane, not an operating division. Titan Haven Group LLC evaluates long-horizon
                            digital asset, media/IP, royalty, infrastructure, and acquisition-oriented opportunities, and
                            may use special-purpose structures where appropriate for particular assets, ventures, or
                            transactions.
                        </p>
                    </Reveal>
                    <div className="mt-10 border-t border-thg-border">
                        {STRUCTURAL.map((s, i) => (
                            <Reveal
                                key={s.name}
                                delay={i * 0.05}
                                className="grid grid-cols-1 md:grid-cols-12 gap-3 py-6 border-b border-thg-border"
                                data-testid={`structural-${i}`}
                            >
                                <h3 className="md:col-span-4 font-display text-lg font-medium text-white">{s.name}</h3>
                                <p className="md:col-span-6 text-sm leading-relaxed text-thg-secondary">{s.summary}</p>
                                <div className="md:col-span-2 md:text-right">
                                    <StatusBadge>{s.status}</StatusBadge>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Active development</h2>
                </Reveal>
                <div className="mt-10 border-t border-thg-border">
                    {ACTIVE_INITIATIVES.filter((item) => item.name !== BUD.name).map((item, i) => (
                        <Reveal key={item.name} delay={i * 0.06} className="grid grid-cols-1 md:grid-cols-12 gap-3 py-6 border-b border-thg-border">
                            <h3 className="md:col-span-4 font-display text-lg font-medium text-white">{item.name}</h3>
                            <p className="md:col-span-6 text-sm leading-relaxed text-thg-secondary">{item.summary}</p>
                            <div className="md:col-span-2 md:text-right">
                                <StatusBadge>{item.status}</StatusBadge>
                            </div>
                        </Reveal>
                    ))}
                </div>
                {/* Bud — pictured, linked evidence card (shared component, cannot drift) */}
                <Reveal delay={0.1} className="mt-8 max-w-2xl">
                    <BudCard />
                </Reveal>
            </ShellSection>
        </PageShell>
    );
}
